import React from 'react'

import {
  Box,
  Button,
  ButtonWithIcon,
  Flex,
} from 'app/components/design-system-components'
import { SearchRounded } from 'app/components/design-system-components/icons/actions'
import { TuneRounded } from 'app/components/design-system-components/icons/image'
import { Input } from 'app/components/design-system-components/inputs'
import { Text } from 'app/components/design-system-components/typography'
import { defaultPropertyFilters } from 'app/dashboard/team_overview_reducer'
import {
  generateListingStatusFilterData,
  getListingStatusFilterState,
} from 'app/features/teams/messages/table-filters/MgmtTypeActionsFilter'
import { getPodsFilterState } from 'app/features/teams/messages/table-filters/PodsFilter'
import { getManagerFilterState } from 'app/features/teams/messages/table-filters/PropertyManagerFilter'
import { parseQueryString, setURLQuery } from 'app/helpers/query_string'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { getTeamSettings } from 'app/services/http/teams/settings'
import { isMobile } from 'app/shared_components/helpers'
import { history } from 'app/shared_components/router'
import { parseDataUtil } from 'app/sm/helpers'
import * as managerViewingsFilters from 'app/sm/manager_viewings/ManagerViewingsFilters'
import {
  agencySortType,
  getViewingSortConfig,
} from 'app/sm/manager_viewings/ManagerViewingsFilters/constants'
import { filtersIds } from 'app/sm/manager_viewings/ManagerViewingsFilters/filters.config'
import { getAgencyListPods } from 'app/sm/team_viewing_preference/access'
import * as Features from 'config/features'

const filterByTextElem = (
  <Text
    as="span"
    fontWeight={theme.fontWeights[6]}
    fontSize={theme.fontSizes.pSmall12}
    color={theme.colors.gray600}
    mr={4}
    minWidth={theme.width[8] + 'px'}
  >
    FILTER BY:{' '}
  </Text>
)

export const PropertySearchContainer = ({ children, componentClass = '' }) => (
  <div className={`properties-search-filter mb0 pb0 ${componentClass}`}>
    {children}
  </div>
)

const FiltersSortActionsElem = ({
  filtersExpanded,
  onFiltersButtonClicked,
  hasFiltersApplied,
  onResetFilters,
  disableReset,
}) => {
  return (
    <Flex alignItems="center" justifyContent="flex-end">
      <ButtonWithIcon
        variant={filtersExpanded ? 'outlineDeepBlue' : 'outline'}
        IconCmp={TuneRounded}
        leading={false}
        mr={theme.space[3] + 'px'}
        onClick={onFiltersButtonClicked}
        iconContainerProps={
          isMobile() && {
            style: {
              marginLeft: 0,
            },
          }
        }
      >
        {!isMobile() && (hasFiltersApplied ? 'Filters Applied' : 'Filter')}
      </ButtonWithIcon>

      {!disableReset && (
        <Button variant="outline" onClick={onResetFilters}>
          Clear
        </Button>
      )}
    </Flex>
  )
}

const SearchInputBox = ({ searchText, onSearchChange, placeholderText }) => {
  return (
    <Input
      icon={
        <SearchRounded
          style={{
            color: theme.colors.gray500,
            width: theme.width[6] + 'px',
            height: theme.width[6] + 'px',
          }}
        />
      }
      inputProps={{
        placeholder: placeholderText,
        value: searchText,
        onChange: onSearchChange,
      }}
      maxWidth={theme.width[30] * 2 + 'px'}
      mr={theme.space[1] + 'px'}
      height={theme.height[18]}
    />
  )
}

export const selectMgmtTypeOptions = ['All', 'N/A', 'New Management', 'Re-let']

export const setFiltersToURLQuery = (filters) => {
  const { manager, workflow, pods, searchText, mgmtType } = filters
  const { pathname } = window.location

  const managerFilter = getManagerFilterState(manager)
  const podsFilter = getPodsFilterState(pods)
  const workflowFilter = managerViewingsFilters.getWorkflowFilterState(workflow)
  const listingStatusFilter = getListingStatusFilterState(mgmtType)

  setURLQuery(history, pathname, {
    manager: managerFilter.length ? managerFilter : undefined,
    pods: podsFilter.length ? podsFilter : undefined,
    workflow: workflowFilter.length ? workflowFilter : undefined,
    listingStatus: listingStatusFilter || undefined,
    searchText,
  })
}

class AgencySearchSectionContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      error: '',
      managerList: [],
      podsList: [],
      filtersExpanded: false,
    }
  }

  componentDidMount() {
    const { currentTeam } = this.props
    this.applyListingStatusQueryFilter(currentTeam)
    this.fetchAndSetManagerList(currentTeam)
    this.fetchAndSetAgencySettings(currentTeam)
    this.fetchAndSetPodsList(currentTeam)
  }

  componentDidUpdate = (prevProps) => {
    const { currentTeam } = this.props
    if (prevProps.currentTeam !== currentTeam) {
      this.applyListingStatusQueryFilter(currentTeam)
      this.fetchAndSetManagerList(currentTeam)
      this.fetchAndSetAgencySettings(currentTeam)
      this.fetchAndSetPodsList(currentTeam)
    }
  }

  applyListingStatusQueryFilter = (currentTeam) => {
    const { filters, setPropertyFilters } = this.props
    if (
      !Features.isPMPropertyListingFilterEnabled(
        currentTeam?.slug,
        currentTeam?.accountType,
      )
    ) {
      return
    }

    const { listingStatus } = parseQueryString(window.location.search)
    const listingStatusFilter = generateListingStatusFilterData({
      listingStatus,
    })

    if (!Object.keys(listingStatusFilter).length) {
      return
    }

    setPropertyFilters({
      mgmtType: {
        ...filters?.mgmtType,
        listingStatus: listingStatusFilter,
      },
    })
  }

  onFilterChange = (filterId, values, isViewingMobileFilter) => {
    const { setPropertyFilters } = this.props

    if (filterId === filtersIds.sort && !isViewingMobileFilter) {
      const { sort } = managerViewingsFilters.getFilters(values)
      if (sort?.[0]) {
        history.push(sort[0])
      }
    }

    let newFilters = {
      [filterId]: values,
    }

    setPropertyFilters(newFilters)
  }

  onResetFilters = () => {
    this.props.resetPropertyFilters()
  }

  onSearchChange = (event) => {
    const { value } = event.target
    const { filters, setPropertyFilters } = this.props
    const newFilters = Object.assign({}, filters)
    newFilters.searchText = value
    setPropertyFilters(newFilters)
  }

  fetchAndSetManagerList = (currentTeam) => {
    const currentTeamGUID = currentTeam ? currentTeam.guid : ''
    if (currentTeamGUID) {
      this.props
        .fetchTeamManagerList(currentTeamGUID)
        .then((managerList) => {
          this.setState({ managerList })
        })
        .catch((error) => {
          this.setState({ error })
        })
    }
  }

  fetchAndSetPodsList = (currentTeam) => {
    const currentTeamGUID = currentTeam ? currentTeam.guid : ''
    if (currentTeamGUID) {
      getAgencyListPods(currentTeamGUID)
        .then((data) => {
          this.setState({ podsList: data })
        })
        .catch((error) => {
          this.setState({ error })
        })
    }
  }

  fetchAndSetAgencySettings = (currentTeam) => {
    const currentTeamGUID = currentTeam ? currentTeam.guid : ''
    if (currentTeamGUID) {
      getTeamSettings(currentTeamGUID, {
        keys: ['bundle'],
      })
        .then(({ settingInfo }) => {
          const { bundle } = settingInfo
          const bundles =
            parseDataUtil.convertObjValueFromStringToBoolean(bundle)

          this.setState({ isPodsDisabled: !bundles?.group_bundle })
        })
        .catch((error) => {
          this.setState({ error })
        })
    }
  }

  render() {
    const {
      filters = defaultPropertyFilters,
      disableSearchBox = false,
      disableSearchManager = false,
      disableSearchStatus = false,
      disableSortby = false,
      disableReset = false,
      disableDatePicker = false,
      enableViewingsSortBy = false,
      enableViewingSearchManager,
      enableUnAssigned = false,
      placeholderText = 'address, name, email, mobile',
      isViewingMobileFilter,
      includeListingStatusFilter = false,
    } = this.props
    const {
      propertyStatus,
      managerList,
      podsList,
      filtersExpanded,
      isPodsDisabled,
    } = this.state
    const teamSlug =
      (this.props.currentTeam && this.props.currentTeam.slug) || ''
    const filtersForRendering = includeListingStatusFilter
      ? filters
      : {
          ...filters,
          mgmtType: {
            ...filters.mgmtType,
            listingStatus: {},
          },
        }

    const enableFilters = [
      !(disableSearchManager && !enableViewingSearchManager) &&
        filtersIds.manager,
      filtersIds.pods,
      (enableViewingsSortBy || !disableSortby) && filtersIds.sort,
      !disableDatePicker && filtersIds.date,
      !disableSearchStatus && filtersIds.workflow,
      filtersIds.mgmtType,
    ]

    const hasFiltersApplied = managerViewingsFilters.hasAppliedFilters(
      filtersForRendering,
      enableFilters,
    )

    const viewingSortOptions =
      enableViewingsSortBy &&
      getViewingSortConfig(agencySortType.viewing, teamSlug)
    const applicationSortOptions =
      !disableSortby &&
      getViewingSortConfig(agencySortType.applications, teamSlug)

    return (
      <Box mt={theme.space[6] + 'px'}>
        <Flex alignItems="center" justifyContent="space-between">
          {!disableSearchBox && (
            <SearchInputBox
              searchText={filters.searchText}
              onSearchChange={this.onSearchChange}
              placeholderText={placeholderText}
            />
          )}

          <FiltersSortActionsElem
            filtersExpanded={filtersExpanded}
            onFiltersButtonClicked={() =>
              this.setState({ filtersExpanded: !filtersExpanded })
            }
            hasFiltersApplied={hasFiltersApplied}
            onResetFilters={this.onResetFilters}
            disableReset={disableReset}
          />
        </Flex>

        <Box mt={theme.space[5] + 'px'}>
          {filtersExpanded && (
            <Flex alignItems="center">
              {filterByTextElem}
              <managerViewingsFilters.ManagerViewingsFilters
                filters={filtersForRendering}
                updateFilter={(filterId, values) =>
                  this.onFilterChange(filterId, values, isViewingMobileFilter)
                }
                pods={podsList}
                managers={managerList}
                viewingSortOptions={viewingSortOptions}
                applicationSortOptions={applicationSortOptions}
                disableSearchManager={
                  disableSearchManager && !enableViewingSearchManager
                }
                disableDatePicker={disableDatePicker}
                disableSearchStatus={disableSearchStatus}
                includePodsFilter={Features.isPodsFilterEnabledForTeam(
                  teamSlug,
                )}
                includeFilterRelets={Features.isFilterReletsEnabledForTeam(
                  teamSlug,
                )}
                includeListingStatusFilter={includeListingStatusFilter}
                isPodsDisabled={isPodsDisabled}
              />
            </Flex>
          )}

          {!filtersExpanded && hasFiltersApplied && (
            <Flex alignItems="center">
              {filterByTextElem}
              <managerViewingsFilters.ManagerViewingsAppliedFilters
                filters={filtersForRendering}
                onDismissFilter={(filterId) =>
                  this.onFilterChange(filterId, {})
                }
                enableFilters={enableFilters}
                includeListingStatusFilter={includeListingStatusFilter}
              />
            </Flex>
          )}
        </Box>
      </Box>
    )
  }
}

export default AgencySearchSectionContainer
